import {
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Loader2,
    X,
} from 'lucide-react';
import React, {
    type ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    getApprovalCopy,
    recommendationDefaultsApprovalSummary,
} from '@apps/web/components/phoebe_chat/approval_copy';
import { usePhoebeChatOrganizationScope } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import {
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import { replaceReferenceTokensWithNames } from '@apps/web/components/phoebe_chat/user_question_model';
import { useDynamicWidgetBuildContext } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widgets_context';
import {
    TEMPLATE_APPROVAL_BANNER_CLASS,
    TEMPLATE_APPROVAL_DOT_CLASS,
    TEMPLATE_CARD_CLASS,
    TEMPLATE_OUTLINE_BUTTON_SM_CLASS,
    TEMPLATE_PRIMARY_BUTTON_SM_CLASS,
    TEMPLATE_TOKEN_CLASS,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/template_theme';
import { ToolApprovalGroupCard } from '@apps/web/components/phoebe_chat_view/tool_calls/grouped_tool_approval_card';
import {
    OutreachStartApprovalWizard,
    parseOutreachStartApprovalArguments,
} from '@apps/web/components/phoebe_chat_view/tool_calls/outreach_approval_wizard';
import { type ToolCardPresentation } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import {
    type ToolApprovalDecision,
    type ToolApprovalDecisionTarget,
    type ToolApprovalGroup,
    type ToolApprovalPresentationGroup,
    type ToolApprovalScope,
    type ToolExecutionEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';
import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { Spinner } from '@apps/web/components/ui/spinner';
import { useOutreachCaregiverSelection } from '@apps/web/context/outreach_caregiver_selection';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { caregiverShiftIdsEqual } from '@apps/web/lib/caregiver_shift_selections';
import { formatTime } from '@apps/web/lib/date_utils';
import { DEFAULT_RECOMMENDATION_DEFAULTS } from '@apps/web/lib/recommendation_defaults';
import { cn } from '@apps/web/lib/utils';
import {
    useOrganization,
    useOrganizationCaregiver,
    useOrganizationClient,
    useOrganizationRecommendationDefaults,
    useShiftSourceSystem,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

function getStringArgument(
    args: ToolExecutionEntry['arguments'],
    key: string,
): string | null {
    return isRecord(args) ? getStringValue(args, key) : null;
}

const COLLAPSED_APPROVAL_DETAIL_COUNT = 5;

function limitPresentationGroups(
    groups: ReadonlyArray<ToolApprovalPresentationGroup>,
    itemLimit: number | null,
): ReadonlyArray<ToolApprovalPresentationGroup> {
    if (itemLimit === null) {
        return groups;
    }

    let remainingItems = itemLimit;
    const visibleGroups: Array<ToolApprovalPresentationGroup> = [];
    for (const group of groups) {
        if (remainingItems === 0) {
            break;
        }
        const items = group.items.slice(0, remainingItems);
        if (items.length > 0) {
            visibleGroups.push({ ...group, items });
            remainingItems -= items.length;
        }
    }
    return visibleGroups;
}

/**
 * Tools whose approval card must not offer "Don't ask again".
 * `send_sms` is in the backend's non-persistable set because a
 * stored grant is dropped on load, so the button would promise nothing.
 * `manage_automation` changes scheduled reviews and requires fresh approval
 * for every mutation.
 */
export const HIDE_ALWAYS_ALLOW_TOOL_NAMES: ReadonlySet<string> = new Set([
    'approve_pending_message',
    'draft_agent_outreach',
    'edit_pending_message',
    'manage_automation',
    'place_voice_call',
    'reject_pending_message',
    'send_sms',
    'write',
]);

function resolveApprovalDetailItem(
    item: string,
    references: ToolExecutionEntry['references'],
): string {
    const resolvedItem = replaceReferenceTokensWithNames(item, references);
    const shiftReference = references?.find(
        (reference) =>
            reference.type.toLowerCase() === 'shift' &&
            item.toLowerCase() === `[[shift:${reference.id.toLowerCase()}]]`,
    );
    const shiftStart = shiftReference?.metadata?.['shift_start_time'];
    const shiftEnd = shiftReference?.metadata?.['shift_end_time'];
    const timezone = shiftReference?.metadata?.['timezone'];
    if (
        typeof shiftStart !== 'string' ||
        typeof shiftEnd !== 'string' ||
        typeof timezone !== 'string'
    ) {
        return resolvedItem;
    }
    return `${resolvedItem}, ${formatTime(
        shiftStart,
        timezone,
        'en-US',
    )} – ${formatTime(shiftEnd, timezone, 'en-US')}`;
}

export function ToolApprovalCard({
    entry,
    isSubmitting,
    onDecision,
    nested = false,
    presentation = 'default',
    organizationName = null,
    approvalPosition,
    approvalCount,
    canGoToPreviousApproval = false,
    canGoToNextApproval = false,
    onPreviousApproval,
    onNextApproval,
}: {
    entry: ToolExecutionEntry;
    isSubmitting: boolean;
    onDecision?: (
        decision: ToolApprovalDecision,
        alwaysAllowScope?: ToolApprovalScope,
        argumentsOverride?: Record<string, unknown>,
    ) => Promise<void>;
    nested?: boolean;
    presentation?: ToolCardPresentation;
    organizationName?: string | null;
    approvalPosition?: number;
    approvalCount?: number;
    canGoToPreviousApproval?: boolean;
    canGoToNextApproval?: boolean;
    onPreviousApproval?: () => void;
    onNextApproval?: () => void;
}) {
    const { formatName } = useFormatPersonName();
    const canSubmit = !!onDecision && !!entry.callId;
    const dynamicWidgetsEnabled = useDynamicWidgetBuildContext() !== null;
    const wizardArguments = parseOutreachStartApprovalArguments(entry);
    const usesMinimalPresentation = presentation === 'minimal';
    const usesTemplatePresentation =
        dynamicWidgetsEnabled && !usesMinimalPresentation;
    const [expandedDetailEntryId, setExpandedDetailEntryId] = useState<
        string | null
    >(null);
    const showAllDetails = expandedDetailEntryId === entry.id;
    const showApprovalNavigation =
        approvalPosition !== undefined &&
        approvalCount !== undefined &&
        approvalCount > 1;
    const { organizationId } = usePhoebeChatOrganizationScope();
    const { selection: liveOutreachSelection } =
        useOutreachCaregiverSelection();
    // Starting an outreach from chat honors the outreach table's live
    // caregiver selection (PHO-15473): when the table's selection differs
    // from what the agent proposed, approving carries the selection as an
    // arguments override, so the agent's own tool executes exactly the
    // selected set and narrates its own start. An explicitly empty table
    // selection blocks starting instead of falling back to the proposal.
    // This surface renders even a single pending approval through
    // ToolApprovalQueueCard, which stages decisions (and their overrides)
    // per item before submitting.
    const proposedStart =
        wizardArguments !== null && wizardArguments.caregiverShiftIds !== null
            ? {
                  outreachId: wizardArguments.outreachId,
                  caregiverShiftIds: wizardArguments.caregiverShiftIds,
              }
            : null;
    const liveSelectionForProposedStart =
        proposedStart !== null &&
        liveOutreachSelection !== null &&
        liveOutreachSelection.outreachId === proposedStart.outreachId
            ? liveOutreachSelection.caregiverShiftIds
            : null;
    const startsWithTableSelection =
        proposedStart !== null &&
        liveSelectionForProposedStart !== null &&
        !caregiverShiftIdsEqual(
            liveSelectionForProposedStart,
            proposedStart.caregiverShiftIds,
        ) &&
        canSubmit;
    const providedOrganizationName =
        approvalOrganizationNameFromProp(organizationName);
    const { data: organization } = useOrganization(
        !providedOrganizationName ? organizationId : null,
        {
            reason: 'tool-approval-card',
        },
    );
    const approvalOrganizationName =
        providedOrganizationName ?? organization?.name ?? null;
    const shiftNoteApprovalShiftId =
        entry.name === 'add_shift_note_to_ehr'
            ? getStringArgument(entry.arguments, 'shift_id')
            : null;
    const { data: shiftSourceSystem } = useShiftSourceSystem(
        organizationId,
        shiftNoteApprovalShiftId,
        { reason: 'tool-approval-card-shift-source' },
    );
    // Fill cards name the caregiver too: approving "the chosen caregiver"
    // tells the coordinator nothing, which is how uninterested caregivers
    // got assigned before (PHO-14544).
    const approvalCaregiverId =
        entry.name === 'save_caregiver_memory_instruction' ||
        entry.name === 'fill_callout_shifts' ||
        entry.name === 'send_sms_to_caregiver' ||
        entry.name === 'send_one_off_sms_in_conversation'
            ? getStringArgument(entry.arguments, 'caregiver_id')
            : null;
    const clientMemoryApprovalId =
        entry.name === 'save_client_memory_instruction'
            ? getStringArgument(entry.arguments, 'client_id')
            : null;
    const { data: approvalCaregiver } = useOrganizationCaregiver(
        approvalCaregiverId ? organizationId : null,
        approvalCaregiverId,
        { reason: 'tool-approval-card-caregiver-target' },
    );
    const { data: approvalClient } = useOrganizationClient(
        clientMemoryApprovalId ? organizationId : null,
        clientMemoryApprovalId,
        { reason: 'tool-approval-card-client-memory-target' },
    );
    const approvalClientName = approvalClient
        ? formatName(`${approvalClient.first_name} ${approvalClient.last_name}`)
        : null;
    // Fill approvals state who will be texted before the coordinator says
    // yes, so the org's notify-non-responders default is fetched only while
    // one of these cards is pending (PHO-14446).
    const isFillShiftsApproval = entry.name === 'fill_callout_shifts';
    const { data: fillApprovalOrganization } = useOrganization(
        isFillShiftsApproval ? organizationId : null,
        {
            reason: 'tool-approval-card-fill-notify-default',
        },
    );
    const notifyNonRespondersDefault =
        fillApprovalOrganization?.general_settings
            ?.default_notify_non_responders_on_fill ?? null;
    const approvalCopyContext = useMemo(
        () => ({
            caregiverName: formatName(approvalCaregiver?.name) || null,
            clientName: approvalClientName,
            organizationName: approvalOrganizationName,
            shiftSourceSystem: shiftSourceSystem?.source_system ?? null,
            notifyNonRespondersDefault,
        }),
        [
            approvalCaregiver?.name,
            approvalClientName,
            approvalOrganizationName,
            notifyNonRespondersDefault,
            shiftSourceSystem?.source_system,
            formatName,
        ],
    );
    const copy = getApprovalCopy(
        entry.name,
        entry.displayArguments ?? entry.arguments,
        approvalCopyContext,
    );
    const approvalPresentation = entry.approvalPresentation;
    const proposalSummary = replaceReferenceTokensWithNames(
        copy.summary,
        entry.references,
    );
    // With a live table selection in effect the button starts the user's
    // selection, not the proposal — the summary must describe that count
    // or the card contradicts itself.
    const selectedStartCount = liveSelectionForProposedStart
        ? Object.keys(liveSelectionForProposedStart).length
        : 0;
    const resolvedSummary = startsWithTableSelection
        ? `Phoebe will start calling and texting ${selectedStartCount} caregiver${
              selectedStartCount === 1 ? '' : 's'
          } for this outreach.`
        : proposalSummary;
    const resolvedDetailList =
        approvalPresentation !== undefined || copy.detailList === undefined
            ? null
            : {
                  ...copy.detailList,
                  items: copy.detailList.items.map((item) =>
                      resolveApprovalDetailItem(item, entry.references),
                  ),
              };
    const visibleDetailItems =
        resolvedDetailList === null || showAllDetails
            ? (resolvedDetailList?.items ?? [])
            : resolvedDetailList.items.slice(
                  0,
                  COLLAPSED_APPROVAL_DETAIL_COUNT,
              );
    const canToggleDetailList =
        resolvedDetailList !== null &&
        resolvedDetailList.items.length > COLLAPSED_APPROVAL_DETAIL_COUNT;
    const presentationItemCount =
        approvalPresentation?.groups.reduce(
            (count, group) => count + group.items.length,
            0,
        ) ?? 0;
    const visiblePresentationGroups = approvalPresentation
        ? limitPresentationGroups(
              approvalPresentation.groups,
              showAllDetails ? null : COLLAPSED_APPROVAL_DETAIL_COUNT,
          )
        : [];
    const canTogglePresentation =
        presentationItemCount > COLLAPSED_APPROVAL_DETAIL_COUNT;
    const resolvedMessagePreview =
        copy.messagePreview === null
            ? null
            : replaceReferenceTokensWithNames(
                  copy.messagePreview,
                  entry.references,
              );
    const isMessageApproval =
        usesTemplatePresentation && resolvedMessagePreview !== null;
    const showAlwaysAllowOptions = !HIDE_ALWAYS_ALLOW_TOOL_NAMES.has(
        entry.name,
    );
    // Recommendation-defaults approvals show "current → new" per field;
    // the org's saved defaults are only fetched while one of these cards
    // is pending.
    const isRecommendationDefaultsApproval =
        entry.name === 'update_recommendation_defaults';
    const { data: currentRecommendationDefaults } =
        useOrganizationRecommendationDefaults(
            isRecommendationDefaultsApproval ? organizationId : null,
        );
    // `undefined` = still loading (generic summary); `null` = the org has
    // no saved row yet, so the effective values are the client defaults.
    const summary =
        isRecommendationDefaultsApproval &&
        entry.arguments &&
        !Array.isArray(entry.arguments)
            ? recommendationDefaultsApprovalSummary(
                  entry.arguments,
                  currentRecommendationDefaults === undefined
                      ? null
                      : (currentRecommendationDefaults ??
                            DEFAULT_RECOMMENDATION_DEFAULTS),
                  approvalCopyContext,
              )
            : resolvedSummary;

    const card = (
        <div
            className={cn(
                'flex w-full flex-col break-words',
                usesTemplatePresentation
                    ? [TEMPLATE_CARD_CLASS, 'max-w-2xl']
                    : usesMinimalPresentation
                      ? 'max-w-[760px] overflow-hidden rounded-[8px] border border-[#DCDCDC] dark:border-border bg-white dark:bg-card shadow-[0_10px_28px_rgba(0,0,0,0.06)]'
                      : 'border-border/50 bg-card max-w-2xl rounded-xl border p-4',
            )}
        >
            {usesTemplatePresentation && approvalPresentation === undefined ? (
                <div className={TEMPLATE_APPROVAL_BANNER_CLASS}>
                    <span className={TEMPLATE_APPROVAL_DOT_CLASS} />
                    Needs your approval
                </div>
            ) : null}
            <div
                className={cn(
                    'flex items-start justify-between gap-3',
                    usesTemplatePresentation
                        ? 'px-4 pt-4'
                        : usesMinimalPresentation &&
                              'border-b border-[#E5E5E5] dark:border-border px-4 py-3',
                )}
            >
                <div className="min-w-0">
                    {usesMinimalPresentation &&
                    approvalPresentation === undefined ? (
                        <div className="dark:text-muted-foreground mb-2 flex items-center gap-1.5 text-[11px] font-medium text-[#6B6B6B]">
                            <span className="size-1.5 rounded-full bg-amber-500" />
                            Tool approval
                        </div>
                    ) : null}
                    <p
                        className={cn(
                            'font-medium',
                            usesTemplatePresentation
                                ? 'text-[15px] leading-[1.35] font-semibold text-[#002429] dark:text-foreground'
                                : usesMinimalPresentation
                                  ? 'text-[14px] text-[#1A1A1A] dark:text-foreground'
                                  : 'text-foreground text-base',
                        )}
                    >
                        {approvalPresentation?.question ??
                            (isMessageApproval ? 'Message draft' : copy.title)}
                    </p>
                    {approvalPresentation === undefined &&
                    (usesMinimalPresentation || usesTemplatePresentation) ? (
                        <p
                            className={cn(
                                'mt-1 text-[13px] leading-5',
                                usesTemplatePresentation
                                    ? 'text-[#617578] dark:text-muted-foreground'
                                    : 'text-[#5A5A5A] dark:text-muted-foreground',
                            )}
                        >
                            {summary}
                        </p>
                    ) : null}
                </div>
                {isMessageApproval ? (
                    <span className={TEMPLATE_TOKEN_CLASS}>SMS</span>
                ) : null}
                {showApprovalNavigation ? (
                    <div
                        className={cn(
                            'flex shrink-0 items-center gap-2 text-sm',
                            usesMinimalPresentation
                                ? 'text-[#6B6B6B] dark:text-muted-foreground'
                                : 'text-muted-foreground',
                        )}
                    >
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className={cn(
                                'h-7 w-7',
                                usesMinimalPresentation &&
                                    'rounded-[6px] text-[#6B6B6B] dark:text-muted-foreground hover:bg-[#F5F5F5] dark:hover:bg-muted hover:text-[#1A1A1A] dark:hover:text-foreground',
                            )}
                            disabled={!canGoToPreviousApproval || isSubmitting}
                            onClick={onPreviousApproval}
                            aria-label="Previous approval"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span>
                            {approvalPosition} of {approvalCount}
                        </span>
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className={cn(
                                'h-7 w-7',
                                usesMinimalPresentation &&
                                    'rounded-[6px] text-[#6B6B6B] dark:text-muted-foreground hover:bg-[#F5F5F5] dark:hover:bg-muted hover:text-[#1A1A1A] dark:hover:text-foreground',
                            )}
                            disabled={!canGoToNextApproval || isSubmitting}
                            onClick={onNextApproval}
                            aria-label="Next approval"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                ) : null}
            </div>
            {approvalPresentation === undefined &&
            !usesMinimalPresentation &&
            !usesTemplatePresentation ? (
                <p className="text-muted-foreground mt-1 text-sm">{summary}</p>
            ) : null}
            {approvalPresentation ? (
                <div
                    className={cn(
                        'mt-3 space-y-3',
                        (usesMinimalPresentation || usesTemplatePresentation) &&
                            'px-4',
                    )}
                >
                    {visiblePresentationGroups.map((group, groupIndex) => (
                        <div key={`${group.heading}-${groupIndex}`}>
                            <p className="text-foreground text-sm font-medium">
                                {group.heading}
                            </p>
                            <ul className="text-muted-foreground mt-1 space-y-1 text-sm">
                                {group.items.map((item, itemIndex) => (
                                    <li
                                        key={`${item}-${itemIndex}`}
                                        className="flex items-start gap-2"
                                    >
                                        <span
                                            aria-hidden={true}
                                            className="mt-[0.55em] size-1.5 shrink-0 rounded-full bg-current opacity-45"
                                        />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                    {canTogglePresentation ? (
                        <button
                            type="button"
                            className={cn(
                                'text-xs font-medium hover:underline',
                                usesTemplatePresentation
                                    ? 'text-[#5533EE] dark:text-violet-300'
                                    : usesMinimalPresentation
                                      ? 'text-[#5A5A5A] dark:text-muted-foreground'
                                      : 'text-primary',
                            )}
                            aria-expanded={showAllDetails}
                            onClick={() => {
                                setExpandedDetailEntryId(
                                    showAllDetails ? null : entry.id,
                                );
                            }}
                        >
                            {showAllDetails
                                ? 'Show fewer'
                                : `View all ${presentationItemCount} details`}
                        </button>
                    ) : null}
                </div>
            ) : null}
            {resolvedDetailList ? (
                <div
                    className={cn(
                        'rounded-lg border',
                        usesTemplatePresentation
                            ? 'mx-4 mt-3 border-[#DBE3E3] dark:border-border bg-[#F7FAFA] dark:bg-muted px-3 py-2.5'
                            : usesMinimalPresentation
                              ? 'mx-4 mt-3 border-[#E5E5E5] dark:border-border bg-[#FAFAFA] dark:bg-muted px-3 py-2.5'
                              : 'border-border/60 bg-muted/30 mt-3 px-3 py-2.5',
                    )}
                >
                    <p
                        className={cn(
                            'font-medium',
                            usesTemplatePresentation
                                ? 'text-xs text-[#42595E] dark:text-muted-foreground'
                                : usesMinimalPresentation
                                  ? 'text-[12px] text-[#5A5A5A] dark:text-muted-foreground'
                                  : 'text-foreground text-sm',
                        )}
                    >
                        {resolvedDetailList.label}
                    </p>
                    <ul
                        className={cn(
                            'mt-1.5 space-y-1',
                            usesTemplatePresentation
                                ? 'text-[13px] leading-5 text-[#42595E] dark:text-muted-foreground'
                                : usesMinimalPresentation
                                  ? 'text-[13px] leading-5 text-[#5A5A5A] dark:text-muted-foreground'
                                  : 'text-muted-foreground text-xs leading-5',
                        )}
                    >
                        {visibleDetailItems.map((item, index) => (
                            <li
                                key={`${item}-${index}`}
                                className="flex items-start gap-2"
                            >
                                <span
                                    aria-hidden={true}
                                    className="mt-[0.55em] size-1.5 shrink-0 rounded-full bg-current opacity-45"
                                />
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                    {canToggleDetailList ? (
                        <button
                            type="button"
                            className={cn(
                                'mt-2 text-xs font-medium hover:underline',
                                usesTemplatePresentation
                                    ? 'text-[#5533EE] dark:text-violet-300'
                                    : usesMinimalPresentation
                                      ? 'text-[#5A5A5A] dark:text-muted-foreground'
                                      : 'text-primary',
                            )}
                            aria-expanded={showAllDetails}
                            onClick={() => {
                                setExpandedDetailEntryId(
                                    showAllDetails ? null : entry.id,
                                );
                            }}
                        >
                            {showAllDetails
                                ? 'Show fewer'
                                : `View all ${resolvedDetailList.items.length} ${resolvedDetailList.itemNoun}s`}
                        </button>
                    ) : null}
                </div>
            ) : null}
            {resolvedMessagePreview ? (
                <div className={cn(isMessageApproval && 'px-4 pt-3')}>
                    {isMessageApproval && approvalCaregiver?.name ? (
                        <p className="dark:text-muted-foreground mb-2 text-xs text-[#617578]">
                            To:{' '}
                            <span className="inline-flex max-w-full items-center rounded-md bg-[#E9FCF0] px-1 py-0.5 text-[13px] font-medium text-[#05614F] dark:bg-emerald-950/50 dark:text-emerald-300">
                                {approvalCaregiver.name}
                            </span>
                        </p>
                    ) : null}
                    <p
                        className={cn(
                            'break-words whitespace-pre-wrap',
                            isMessageApproval
                                ? 'inline-block w-fit max-w-[85%] rounded-xl rounded-bl bg-[#F0F5F5] dark:bg-muted px-3 py-2 text-[13px] leading-[1.4] [overflow-wrap:anywhere] text-[#002429] dark:text-foreground'
                                : usesMinimalPresentation
                                  ? 'text-[13px] leading-5 text-[#1A1A1A] dark:text-foreground'
                                  : cn(
                                        'text-foreground text-sm',
                                        usesTemplatePresentation
                                            ? 'rounded-[10px] border border-[#DBE3E3] dark:border-border bg-[#F7FAFA] dark:bg-muted p-3'
                                            : 'bg-muted mt-3 rounded-lg p-3',
                                    ),
                        )}
                    >
                        {resolvedMessagePreview}
                    </p>
                </div>
            ) : null}
            <div
                className={cn(
                    'flex flex-wrap items-center gap-2',
                    usesTemplatePresentation
                        ? 'mt-4 justify-between border-t border-[#E9F0F0] dark:border-border bg-[#F7FAFA] dark:bg-muted px-3.5 py-2.5'
                        : usesMinimalPresentation
                          ? 'mt-3 justify-between border-t border-[#E5E5E5] dark:border-border px-4 py-3'
                          : 'mt-3 justify-end pt-1',
                )}
            >
                {usesMinimalPresentation || usesTemplatePresentation ? (
                    <span
                        className={cn(
                            'text-[11px] font-medium',
                            usesTemplatePresentation
                                ? 'text-[#617578] dark:text-muted-foreground'
                                : 'text-[#8A8A8A] dark:text-muted-foreground',
                        )}
                    >
                        {isMessageApproval
                            ? 'Review before this message is sent'
                            : 'Review before this action runs'}
                    </span>
                ) : null}
                <div className="flex flex-wrap items-center justify-end gap-2">
                    <button
                        type="button"
                        className={cn(
                            usesTemplatePresentation
                                ? TEMPLATE_OUTLINE_BUTTON_SM_CLASS
                                : 'border-input bg-background hover:bg-accent hover:text-accent-foreground focus-visible:border-ring focus-visible:ring-ring/50 inline-flex h-8 items-center justify-center gap-1.5 rounded-md border px-3 text-sm font-medium shadow-xs transition-all focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50',
                            usesMinimalPresentation &&
                                'rounded-[6px] border-[#DCDCDC] dark:border-border bg-white dark:bg-card px-2.5 text-xs text-[#1A1A1A] dark:text-foreground shadow-none hover:bg-[#F5F5F5] dark:hover:bg-muted hover:text-[#1A1A1A] dark:hover:text-foreground',
                        )}
                        onClick={() => {
                            if (onDecision) {
                                void onDecision('deny');
                            }
                        }}
                        disabled={isSubmitting || !canSubmit}
                    >
                        {usesMinimalPresentation ? (
                            <X className="size-3.5" />
                        ) : null}
                        {isMessageApproval ? 'Don’t send' : 'Deny'}
                    </button>
                    {isMessageApproval ? (
                        <button
                            type="button"
                            className={TEMPLATE_PRIMARY_BUTTON_SM_CLASS}
                            onClick={() => {
                                if (onDecision) {
                                    void onDecision('allow_once');
                                }
                            }}
                            disabled={isSubmitting || !canSubmit}
                        >
                            {isSubmitting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : null}
                            Send message
                        </button>
                    ) : startsWithTableSelection ? (
                        <Button
                            type="button"
                            size="sm"
                            variant="default"
                            className={cn(
                                usesTemplatePresentation &&
                                    'h-8 rounded-full border-transparent bg-[#5533EE] px-3.5 text-[13px] text-white shadow-[0_1px_2px_rgba(0,36,41,0.08)] hover:bg-[#4629C7]',
                                usesMinimalPresentation &&
                                    'h-8 gap-1.5 rounded-[6px] bg-[#1A1A1A] px-3 text-xs font-medium text-white shadow-none hover:bg-black',
                            )}
                            onClick={() => {
                                if (
                                    onDecision &&
                                    liveSelectionForProposedStart !== null &&
                                    Object.keys(liveSelectionForProposedStart)
                                        .length > 0
                                ) {
                                    void onDecision('allow_once', undefined, {
                                        caregiver_shift_ids:
                                            liveSelectionForProposedStart,
                                    });
                                }
                            }}
                            disabled={
                                isSubmitting ||
                                Object.keys(liveSelectionForProposedStart ?? {})
                                    .length === 0
                            }
                        >
                            {isSubmitting ? <Spinner size="xs" /> : null}
                            Start{' '}
                            {
                                Object.keys(liveSelectionForProposedStart ?? {})
                                    .length
                            }{' '}
                            selected
                        </Button>
                    ) : (
                        <div className="flex items-center">
                            <Button
                                type="button"
                                size="sm"
                                variant="default"
                                className={cn(
                                    showAlwaysAllowOptions && 'rounded-r-none',
                                    usesTemplatePresentation && [
                                        'h-8 border-transparent bg-[#5533EE] px-3.5 text-[13px] text-white shadow-[0_1px_2px_rgba(0,36,41,0.08)] hover:bg-[#4629C7]',
                                        showAlwaysAllowOptions
                                            ? 'rounded-l-full'
                                            : 'rounded-full',
                                    ],
                                    usesMinimalPresentation && [
                                        'h-8 gap-1.5 rounded-[6px] bg-[#1A1A1A] px-3 text-xs font-medium text-white shadow-none hover:bg-black',
                                        showAlwaysAllowOptions &&
                                            'rounded-r-none',
                                    ],
                                )}
                                onClick={() => {
                                    if (onDecision) {
                                        void onDecision('allow_once');
                                    }
                                }}
                                disabled={isSubmitting || !canSubmit}
                            >
                                {isSubmitting ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <React.Fragment>
                                        {usesMinimalPresentation ? (
                                            <Check className="size-3.5" />
                                        ) : null}
                                        Allow
                                    </React.Fragment>
                                )}
                            </Button>
                            {showAlwaysAllowOptions ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild={true}>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="default"
                                            aria-label="Allow options"
                                            className={cn(
                                                'rounded-l-none border-l border-l-white/20 px-1.5',
                                                usesTemplatePresentation &&
                                                    'h-8 rounded-r-full bg-[#5533EE] text-white shadow-[0_1px_2px_rgba(0,36,41,0.08)] hover:bg-[#4629C7]',
                                                usesMinimalPresentation &&
                                                    'h-8 rounded-[6px] rounded-l-none bg-[#1A1A1A] text-xs font-medium text-white shadow-none hover:bg-black',
                                            )}
                                            disabled={
                                                isSubmitting || !canSubmit
                                            }
                                        >
                                            <ChevronDown className="size-3.5" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuLabel className="text-muted-foreground text-xs">
                                            Don’t ask again
                                        </DropdownMenuLabel>
                                        <DropdownMenuItem
                                            onSelect={() => {
                                                if (onDecision) {
                                                    void onDecision(
                                                        'always_allow',
                                                        'conversation',
                                                    );
                                                }
                                            }}
                                        >
                                            This chat
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onSelect={() => {
                                                if (onDecision) {
                                                    void onDecision(
                                                        'always_allow',
                                                        'user',
                                                    );
                                                }
                                            }}
                                        >
                                            All my chats
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : null}
                        </div>
                    )}
                </div>
                {startsWithTableSelection ? (
                    <p className="text-muted-foreground mt-2 text-right text-xs">
                        {Object.keys(liveSelectionForProposedStart ?? {})
                            .length === 0
                            ? 'Your table selection is empty — select at least one caregiver in the outreach table to start.'
                            : `Uses your current table selection (${
                                  Object.keys(
                                      liveSelectionForProposedStart ?? {},
                                  ).length
                              }) instead of the proposed list (${
                                  Object.keys(
                                      proposedStart?.caregiverShiftIds ?? {},
                                  ).length
                              }).`}
                    </p>
                ) : null}
            </div>
        </div>
    );

    // The outreach-start approval renders as the template catalog's
    // four-step wizard when dynamic widgets are on; the classic card stays
    // the fallback for unparsable args and read-only surfaces.
    if (dynamicWidgetsEnabled && wizardArguments !== null && canSubmit) {
        const wizard = (
            <OutreachStartApprovalWizard
                entry={entry}
                wizardArguments={wizardArguments}
                isSubmitting={isSubmitting}
                onDecision={(decision, argumentsOverride) =>
                    onDecision?.(decision, undefined, argumentsOverride)
                }
            />
        );
        return nested ? (
            wizard
        ) : (
            <div className="flex justify-start">{wizard}</div>
        );
    }

    if (nested) {
        return card;
    }

    if (usesMinimalPresentation) {
        return (
            <div className="ml-7 flex w-[calc(100%-1.75rem)] max-w-full justify-start">
                {card}
            </div>
        );
    }

    return <div className="flex justify-start">{card}</div>;
}

function getApprovalEntryKey(entry: ToolExecutionEntry): string {
    return entry.callId ?? entry.id;
}

type ToolApprovalQueueItem =
    | {
          readonly kind: 'single';
          readonly key: string;
          readonly entry: ToolExecutionEntry;
      }
    | {
          readonly kind: 'group';
          readonly key: string;
          readonly group: ToolApprovalGroup;
          readonly entries: ReadonlyArray<ToolExecutionEntry>;
      };

function buildToolApprovalQueueItems(
    entries: ReadonlyArray<ToolExecutionEntry>,
): Array<ToolApprovalQueueItem> {
    const items: Array<ToolApprovalQueueItem> = [];
    const seenGroupIds = new Set<string>();

    for (const entry of entries) {
        const group = entry.approvalGroup;
        if (group && group.callIds.length > 1) {
            if (seenGroupIds.has(group.id)) {
                continue;
            }
            seenGroupIds.add(group.id);
            items.push({
                kind: 'group',
                key: `group:${group.id}`,
                group,
                entries: entries.filter(
                    (candidate) => candidate.approvalGroup?.id === group.id,
                ),
            });
            continue;
        }

        items.push({
            kind: 'single',
            key: getApprovalEntryKey(entry),
            entry,
        });
    }

    return items;
}

function getApprovalItemCallIds(
    item: ToolApprovalQueueItem,
): ReadonlyArray<string> {
    if (item.kind === 'group') {
        return item.group.callIds;
    }
    return item.entry.callId ? [item.entry.callId] : [];
}

function isApprovalItemSubmitting(
    item: ToolApprovalQueueItem,
    decidingCallIds: Record<string, boolean>,
): boolean {
    return getApprovalItemCallIds(item).some(
        (callId) => decidingCallIds[callId] === true,
    );
}

/** Length of the fade below. Kept in sync with the `duration-200` class. */
const APPROVAL_REVEAL_DURATION_MS = 200;

/**
 * Holds the queue's footprint steady while one approval swaps for the next.
 *
 * Staging a decision unmounts the current card and mounts the next one, which
 * are rarely the same height — without this the transcript snapped to the new
 * height and the next approval appeared with no transition. The frame animates
 * its own height between the two, and the incoming card fades in. Height is not
 * clipped: a taller card overhangs for the length of the fade rather than
 * cutting off the card's shadow in steady state.
 */
function ToolApprovalRevealFrame({
    revealKey,
    children,
}: {
    readonly revealKey: string;
    readonly children: ReactNode;
}) {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [contentHeightPx, setContentHeightPx] = useState<number | null>(null);
    const [isRevealing, setIsRevealing] = useState(true);

    useEffect(() => {
        const content = contentRef.current;
        if (content === null || typeof ResizeObserver === 'undefined') {
            return;
        }
        const syncHeight = () => {
            setContentHeightPx(content.getBoundingClientRect().height);
        };
        syncHeight();
        const resizeObserver = new ResizeObserver(syncHeight);
        resizeObserver.observe(content);
        return () => resizeObserver.disconnect();
    }, []);

    // Staging a decision advances to the next approval synchronously, so the
    // incoming card mounts under the cursor while it is still transparent. A
    // timer (not `onAnimationEnd`) because reduced motion skips the animation
    // entirely, which would leave the card permanently unclickable.
    useEffect(() => {
        setIsRevealing(true);
        const revealTimer = setTimeout(
            () => setIsRevealing(false),
            APPROVAL_REVEAL_DURATION_MS,
        );
        return () => clearTimeout(revealTimer);
    }, [revealKey]);

    return (
        <div
            className="transition-[height] duration-200 ease-out motion-reduce:transition-none"
            style={
                contentHeightPx === null
                    ? undefined
                    : { height: contentHeightPx }
            }
        >
            <div ref={contentRef}>
                <div
                    key={revealKey}
                    className={cn(
                        'animate-in fade-in-0 duration-200 motion-reduce:animate-none',
                        // Don't let a second click from the card being approved
                        // land on the tool call that replaces it.
                        isRevealing && 'pointer-events-none',
                    )}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}

export function ToolApprovalQueueCard({
    entries,
    decidingCallIds,
    onDecision,
    presentation = 'default',
    organizationName = null,
}: {
    entries: ReadonlyArray<ToolExecutionEntry>;
    decidingCallIds: Record<string, boolean>;
    onDecision?: (
        target: ToolApprovalDecisionTarget,
        decision: ToolApprovalDecision,
        alwaysAllowScope?: ToolApprovalScope,
        argumentsOverride?: Record<string, unknown>,
    ) => Promise<void>;
    presentation?: ToolCardPresentation;
    organizationName?: string | null;
}) {
    const approvalItems = useMemo(
        () => buildToolApprovalQueueItems(entries),
        [entries],
    );
    const [activeApprovalKey, setActiveApprovalKey] = useState<string | null>(
        () => approvalItems[0]?.key ?? null,
    );
    const [stagedApprovalDecisions, setStagedApprovalDecisions] = useState<
        Record<string, ToolApprovalDecision>
    >({});
    const [isSubmittingStagedApprovals, setIsSubmittingStagedApprovals] =
        useState(false);
    const isSubmittingStagedApprovalsRef = useRef(false);
    const submittedApprovalKeysRef = useRef<Record<string, boolean>>({});
    const stagedApprovalScopesRef = useRef<
        Record<string, ToolApprovalScope | undefined>
    >({});
    const stagedApprovalArgumentsOverridesRef = useRef<
        Record<string, Record<string, unknown> | undefined>
    >({});

    useEffect(() => {
        const currentItemKeys = new Set(approvalItems.map((item) => item.key));
        submittedApprovalKeysRef.current = Object.fromEntries(
            Object.entries(submittedApprovalKeysRef.current).filter(([key]) =>
                currentItemKeys.has(key),
            ),
        );
        stagedApprovalScopesRef.current = Object.fromEntries(
            Object.entries(stagedApprovalScopesRef.current).filter(([key]) =>
                currentItemKeys.has(key),
            ),
        );
        stagedApprovalArgumentsOverridesRef.current = Object.fromEntries(
            Object.entries(stagedApprovalArgumentsOverridesRef.current).filter(
                ([key]) => currentItemKeys.has(key),
            ),
        );
        setStagedApprovalDecisions((current) => {
            const next = Object.fromEntries(
                Object.entries(current).filter(([key]) =>
                    currentItemKeys.has(key),
                ),
            );
            return Object.keys(next).length === Object.keys(current).length
                ? current
                : next;
        });
    }, [approvalItems]);

    const visibleItems = approvalItems.filter(
        (item) => stagedApprovalDecisions[item.key] === undefined,
    );
    const hasUnsubmittedStagedApprovals = approvalItems.some((item) => {
        return (
            stagedApprovalDecisions[item.key] !== undefined &&
            !submittedApprovalKeysRef.current[item.key]
        );
    });
    const isWaitingForApprovalRefresh =
        approvalItems.length > 0 &&
        visibleItems.length === 0 &&
        !hasUnsubmittedStagedApprovals;
    const activeVisibleIndex = Math.max(
        0,
        visibleItems.findIndex((item) => item.key === activeApprovalKey),
    );
    const activeItem =
        visibleItems[activeVisibleIndex] ??
        visibleItems[0] ??
        (isWaitingForApprovalRefresh
            ? (approvalItems[approvalItems.length - 1] ?? null)
            : null);
    const activeDisplayIndex = activeItem
        ? approvalItems.findIndex((item) => item.key === activeItem.key)
        : -1;

    useEffect(() => {
        if (visibleItems.length === 0) {
            setActiveApprovalKey(null);
            return;
        }
        if (
            activeApprovalKey === null ||
            !visibleItems.some((item) => item.key === activeApprovalKey)
        ) {
            const firstVisibleItem = visibleItems[0];
            if (firstVisibleItem) {
                setActiveApprovalKey(firstVisibleItem.key);
            }
        }
    }, [activeApprovalKey, visibleItems]);

    if (!activeItem || activeDisplayIndex < 0) {
        return null;
    }

    const goToVisibleApproval = (direction: -1 | 1) => {
        const nextVisibleIndex = Math.min(
            Math.max(activeVisibleIndex + direction, 0),
            visibleItems.length - 1,
        );
        const nextItem = visibleItems[nextVisibleIndex];
        if (nextItem) {
            setActiveApprovalKey(nextItem.key);
        }
    };

    const movePastCurrentApproval = (
        nextStagedApprovalDecisions: Record<string, ToolApprovalDecision>,
    ) => {
        const activeItemKey = activeItem.key;
        const remainingItems = visibleItems.filter(
            (item) => item.key !== activeItemKey,
        );
        const nextItem =
            remainingItems[
                Math.min(activeVisibleIndex, remainingItems.length - 1)
            ] ??
            remainingItems[remainingItems.length - 1] ??
            null;
        setStagedApprovalDecisions(nextStagedApprovalDecisions);
        setActiveApprovalKey(nextItem ? nextItem.key : null);
    };

    const submitStagedApprovalDecisions = async (
        nextStagedApprovalDecisions: Record<string, ToolApprovalDecision>,
    ) => {
        if (!onDecision) {
            return;
        }
        if (isSubmittingStagedApprovalsRef.current) {
            return;
        }
        const decisionsToSubmit = approvalItems.flatMap((item) => {
            const decision = nextStagedApprovalDecisions[item.key];
            const callIds = getApprovalItemCallIds(item);
            if (
                callIds.length === 0 ||
                decision === undefined ||
                submittedApprovalKeysRef.current[item.key]
            ) {
                return [];
            }
            return [
                {
                    target:
                        item.kind === 'group' ? callIds : (callIds[0] ?? ''),
                    decision,
                    itemKey: item.key,
                    alwaysAllowScope: stagedApprovalScopesRef.current[item.key],
                    argumentsOverride:
                        stagedApprovalArgumentsOverridesRef.current[item.key],
                },
            ];
        });

        if (decisionsToSubmit.length === 0) {
            return;
        }

        isSubmittingStagedApprovalsRef.current = true;
        setIsSubmittingStagedApprovals(true);
        try {
            for (const {
                target,
                decision,
                itemKey,
                alwaysAllowScope,
                argumentsOverride,
            } of decisionsToSubmit) {
                if (argumentsOverride === undefined) {
                    await onDecision(target, decision, alwaysAllowScope);
                } else {
                    await onDecision(
                        target,
                        decision,
                        alwaysAllowScope,
                        argumentsOverride,
                    );
                }
                submittedApprovalKeysRef.current = {
                    ...submittedApprovalKeysRef.current,
                    [itemKey]: true,
                };
            }
            setStagedApprovalDecisions(nextStagedApprovalDecisions);
            setActiveApprovalKey(null);
        } catch {
            // Decision handlers own the user-facing error toast. Leave the
            // approval staged locally so the card can be retried.
        } finally {
            isSubmittingStagedApprovalsRef.current = false;
            setIsSubmittingStagedApprovals(false);
        }
    };

    const stageActiveApprovalDecision = async (
        decision: ToolApprovalDecision,
        alwaysAllowScope?: ToolApprovalScope,
        argumentsOverride?: Record<string, unknown>,
    ) => {
        stagedApprovalScopesRef.current[activeItem.key] = alwaysAllowScope;
        stagedApprovalArgumentsOverridesRef.current[activeItem.key] =
            argumentsOverride;
        const nextStagedApprovalDecisions = {
            ...stagedApprovalDecisions,
            [activeItem.key]: decision,
        };
        const isLastVisibleApproval = visibleItems.length <= 1;
        if (!isLastVisibleApproval) {
            movePastCurrentApproval(nextStagedApprovalDecisions);
            return;
        }
        await submitStagedApprovalDecisions(nextStagedApprovalDecisions);
    };

    if (activeItem.kind === 'group') {
        return (
            <ToolApprovalRevealFrame revealKey={activeItem.key}>
                <ToolApprovalGroupCard
                    group={activeItem.group}
                    isSubmitting={
                        isSubmittingStagedApprovals ||
                        isApprovalItemSubmitting(activeItem, decidingCallIds)
                    }
                    onDecision={
                        onDecision && !isWaitingForApprovalRefresh
                            ? stageActiveApprovalDecision
                            : undefined
                    }
                    presentation={presentation}
                    approvalPosition={activeDisplayIndex + 1}
                    approvalCount={approvalItems.length}
                    canGoToPreviousApproval={
                        !isWaitingForApprovalRefresh && activeVisibleIndex > 0
                    }
                    canGoToNextApproval={
                        !isWaitingForApprovalRefresh &&
                        activeVisibleIndex < visibleItems.length - 1
                    }
                    onPreviousApproval={() => goToVisibleApproval(-1)}
                    onNextApproval={() => goToVisibleApproval(1)}
                />
            </ToolApprovalRevealFrame>
        );
    }

    return (
        <ToolApprovalRevealFrame revealKey={activeItem.key}>
            <ToolApprovalCard
                entry={activeItem.entry}
                isSubmitting={
                    isSubmittingStagedApprovals ||
                    isApprovalItemSubmitting(activeItem, decidingCallIds)
                }
                onDecision={
                    activeItem.entry.callId &&
                    onDecision &&
                    !isWaitingForApprovalRefresh
                        ? stageActiveApprovalDecision
                        : undefined
                }
                presentation={presentation}
                organizationName={organizationName}
                approvalPosition={activeDisplayIndex + 1}
                approvalCount={approvalItems.length}
                canGoToPreviousApproval={
                    !isWaitingForApprovalRefresh && activeVisibleIndex > 0
                }
                canGoToNextApproval={
                    !isWaitingForApprovalRefresh &&
                    activeVisibleIndex < visibleItems.length - 1
                }
                onPreviousApproval={() => goToVisibleApproval(-1)}
                onNextApproval={() => goToVisibleApproval(1)}
            />
        </ToolApprovalRevealFrame>
    );
}

const APPROVAL_ORGANIZATION_NAME_PLACEHOLDERS = new Set([
    'Loading organization...',
    'Unavailable organization',
]);

function approvalOrganizationNameFromProp(
    organizationName: string | null | undefined,
): string | null {
    const trimmedOrganizationName = organizationName?.trim();
    if (!trimmedOrganizationName) {
        return null;
    }
    if (APPROVAL_ORGANIZATION_NAME_PLACEHOLDERS.has(trimmedOrganizationName)) {
        return null;
    }
    return trimmedOrganizationName;
}
